import { ColDef, ICellRendererParams, IHeaderParams } from 'ag-grid-enterprise';
import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { asyncManageComponentsCallbacks } from '../asyncManageComponentsCallbacks';
import { DeleteComponentButton } from '../CellRenderers/cellRenderers';
import { ComponentCellRendererProps } from '../CellRenderers/types';
import {
  ArtificialColumnDefinitionArdoqMetadata,
  InventoryContext,
} from '../types';
import { Checkbox } from '@ardoq/forms';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { t } from '@ardoq/i18n';

export const AG_GRID_SELECTION_COLUMN_ID = 'ag-Grid-SelectionColumn';

const SelectionColumnHeader = (
  props: IHeaderParams<APIComponentAttributes, InventoryContext>
) => {
  const i18nLocale = props.context?.i18nLocale ?? {};
  return (
    <VisuallyHidden>
      {t(
        i18nLocale,
        'inventoryApp:ag.selectionColumn.header',
        'Select component'
      )}
    </VisuallyHidden>
  );
};

const SelectOrDeleteCellRenderer = (
  params: ComponentCellRendererProps<ArdoqId>
) => {
  if (!params.data) {
    return '';
  }

  if (!params.node.rowPinned) {
    // Selection doesn't work for pinned rows, like the newly created components
    return (
      <Checkbox
        isDisabled={!params.node.selectable}
        isChecked={Boolean(params.node.isSelected())}
        preventDefault={true} // needed for onShiftClick
        onShiftClick={handleOnShiftClick(params)}
        onChange={value => params.node.setSelected(value)}
      >
        <VisuallyHidden>{params.data.name}</VisuallyHidden>
      </Checkbox>
    );
  }

  return (
    <DeleteComponentButton
      onClick={() =>
        asyncManageComponentsCallbacks.deleteComponentAndRemoveFromGrid(
          params.data!._id,
          params.api,
          params.context.commands
        )
      }
      {...params}
    />
  );
};

export const SELECTION_COLUMN_DEFINITION: ColDef<APIComponentAttributes> = {
  headerComponent: SelectionColumnHeader,
  sortable: false,
  suppressColumnsToolPanel: true,
  suppressNavigable: false,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  suppressFillHandle: true,
  filter: false,
  suppressSizeToFit: true,
  width: 50, // Ensure space remains the same when the checkbox is hidden
  context: {
    inventoryColumnType: 'artificial',
  } satisfies ArtificialColumnDefinitionArdoqMetadata,
  cellRenderer: SelectOrDeleteCellRenderer,
};

const handleOnShiftClick = (
  params: ICellRendererParams<APIComponentAttributes, string, InventoryContext>
) => {
  return () => {
    const selectedNodes = params.api.getSelectedNodes();
    const lastSelectedRowIndex =
      selectedNodes[selectedNodes.length - 1]?.rowIndex ?? null;
    const currentRowIndex = params.node.rowIndex;
    if (lastSelectedRowIndex === null || currentRowIndex === null) {
      return;
    }

    const selectionStart = Math.min(lastSelectedRowIndex, currentRowIndex);
    const selectionEnd = Math.max(lastSelectedRowIndex, currentRowIndex);
    const wasPreviouslySelected = params.node.isSelected();

    for (let i = selectionStart; i <= selectionEnd; i++) {
      const node = params.api.getDisplayedRowAtIndex(i);
      if (node) {
        node.setSelected(!wasPreviouslySelected);
      }
    }
  };
};
